/**
 * Schema.org structured data generators
 * For SEO rich snippets in Google search results
 */

const SITE_URL = 'https://iloveoph.com';
const ORGANIZATION_NAME = 'The Original Pancake House';

/**
 * Generate Organization schema for the homepage
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: ORGANIZATION_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/images/oph-logo.svg`,
  description: 'Serving made-from-scratch breakfast and brunch since 1953. Experience fresh ingredients, handcrafted recipes, and warm hospitality.',
  foundingDate: '1953',
  sameAs: [
    'https://www.facebook.com/OriginalPancakeHouse',
    'https://www.instagram.com/originalpancakehouse',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    availableLanguage: 'English',
  },
});

/**
 * Generate Restaurant schema for a location
 * @param {Object} location - Location data from Sanity
 */
export const generateRestaurantSchema = (location) => {
  if (!location) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': `${SITE_URL}/locations/${location.slug}`,
    name: `${ORGANIZATION_NAME} - ${location.name}`,
    url: `${SITE_URL}/locations/${location.slug}`,
    image: location.featuredImage || `${SITE_URL}/oph-social-share3.jpg`,
    telephone: location.phoneNumber,
    servesCuisine: ['American', 'Breakfast', 'Brunch'],
    priceRange: '$$',
    acceptsReservations: 'False',
  };

  // Add address if available
  if (location.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: location.address.street,
      addressLocality: location.address.city,
      addressRegion: location.address.state,
      postalCode: location.address.zipCode,
      addressCountry: 'US',
    };
  }

  // Add coordinates if available
  if (location.coordinates) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: location.coordinates.lat,
      longitude: location.coordinates.lng,
    };
  }

  // Add opening hours if available
  if (location.hoursOfOperation && location.hoursOfOperation.length > 0) {
    schema.openingHoursSpecification = location.hoursOfOperation
      .filter(hours => !hours.isClosed)
      .map(hours => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: hours.day,
        opens: hours.openTime,
        closes: hours.closeTime,
      }));
  }

  return schema;
};

/**
 * Generate BreadcrumbList schema
 * @param {Array} breadcrumbs - Array of {name, url} objects
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`,
    })),
  };
};

/**
 * Generate MenuItem schema
 * @param {Object} item - Menu item data from Sanity
 * @param {string} categorySlug - Category slug for URL
 */
export const generateMenuItemSchema = (item, categorySlug) => {
  if (!item) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MenuItem',
    name: item.name,
    description: item.plainDescription || item.description,
    url: `${SITE_URL}/menu/${categorySlug}/${item.slug}`,
  };

  if (item.imageUrl) {
    schema.image = item.imageUrl;
  }

  if (item.calories) {
    schema.nutrition = {
      '@type': 'NutritionInformation',
      calories: `${item.calories} calories`,
    };
  }

  return schema;
};

/**
 * Generate FAQPage schema
 * @param {Array} faqs - Array of {question, answer} objects
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate WebSite schema for homepage with search action
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: ORGANIZATION_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/menu?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});


